// src/App.js
import React, { useState, useContext, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import Activities from "./components/Activities";
import Stock from "./components/Stock";
import Shipping from "./components/Shipping";
import Login from "./components/Login";
import Settings from "./components/Settings";
import WorkerManager from "./components/WorkerManager";
import Cookies from "js-cookie";
import { CssBaseline, Box, ThemeProvider, GlobalStyles } from "@mui/material";
import "antd/dist/reset.css";
import { darkTheme } from "./themes/themes";
import { AuthProvider, useAuth } from "./components/AuthContext"; // Fix import to include useAuth
import StatusBar from "./components/StatusBar";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { SettingsContext, SettingsProvider } from "./components/SettingsContext";
import FloatingSettingsPanel from "./components/FloatingSettingsPanel";
import { ChatProvider, useChat } from './components/_social/ChatContext'; // Fix import
import ChatPanel from './components/_social/ChatPanel'; // Fix import
import { ActivityProvider } from "./components/_activities/ActivityContext";
import SidebarToggleButton from './components/SidebarToggleButton';
import SuperAdmin from "./components/_admin/SuperAdmin";
import RepairCostChecker from "./components/RepairCostChecker";
import PriceAdminPanel from "./components/_admin/PriceAdminPanel";
import useRoleSpecificSettings from "./hooks/useRoleSpecificSettings";
import Social from "./components/Social";
import axios from "axios"; // Add axios import for API calls
import { DashboardDataProvider } from './contexts/DashboardDataContext'; // Add import for DashboardDataProvider
import emergencyRecovery from './utils/emergencyRecovery'; // Add the import for emergency recovery system
import EsdCheck from './components/EsdCheck'; // Add import for EsdCheck
import EsdOverview from './components/EsdOverview.js'; // Add import for EsdOverview
import Front from './components/developer/Front';
import Back from './components/developer/Back';
import Feedback from './components/developer/Feedback'; // Import Feedback component
import FeedbackReader from './components/developer/FeedbackReader'; // Import FeedbackReader component
import WebOrkDashboard from "./components/WebOrkDashboard"; // Import the WebOrk Dashboard component

// Constants for activity tracking
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const STATUS_UPDATE_INTERVAL = 2 * 60 * 1000; // Update status every 2 minutes

const themeToUse = darkTheme;

const App = () => {
  // Initialize recovery system
  React.useEffect(() => {
    emergencyRecovery.initializeRecovery();
  }, []);

  return (
    <ThemeProvider theme={themeToUse}>
      <CssBaseline />
      <GlobalStyles 
        styles={(theme) => ({
          'body:has(#dashboard-page)': {
            overflow: 'hidden !important'
          },
          '#dashboard-page': {
            overflow: 'hidden !important'
          }
        })}
      />
      <Router>
        <Routes>
          {/* Public route for ESD Check */}
          <Route path="/esd-check" element={<EsdCheck />} />
          
          {/* All other routes */}
          <Route path="*" element={
            <AuthProvider>
              <SettingsProvider>
                <DashboardDataProvider>
                  <ChatProvider>
                    <ActivityProvider>
                      <MainLayout />
                    </ActivityProvider>
                  </ChatProvider>
                </DashboardDataProvider>
              </SettingsProvider>
            </AuthProvider>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

// Component to apply role-specific settings
const RoleSettingsHandler = () => {
  useRoleSpecificSettings();
  return null;
};

// New component to track user activity - Fix the context usage
const UserActivityTracker = () => {
  const { user, getToken, isAuthenticated } = useAuth(); // Use the proper hook from AuthContext
  const [lastActivity, setLastActivity] = useState(new Date());
  const [isOnline, setIsOnline] = useState(true);
  const activityTimerRef = React.useRef(null);
  const statusUpdateTimerRef = React.useRef(null);

  // Function to update online status in the backend - Fix the token handling
  const updateOnlineStatus = async (status) => {
    if (!isAuthenticated) return;
    try {
      // Get token directly from cookie instead of using getToken
      const token = Cookies.get('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL || '';
      await axios.post(
        `${apiUrl}/user/update_status`,
        { is_online: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(`User online status updated: ${status ? 'online' : 'offline'}`);
    } catch (error) {
      console.error('Failed to update online status:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  // Reset the inactivity timer when user is active
  const handleUserActivity = () => {
    const now = new Date();
    setLastActivity(now);
    
    // If user was marked offline, mark them as online again
    if (!isOnline) {
      setIsOnline(true);
      updateOnlineStatus(true);
    }

    // Reset the inactivity timer
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }
    
    // Set new timer for inactivity
    activityTimerRef.current = setTimeout(() => {
      setIsOnline(false);
      updateOnlineStatus(false);
    }, INACTIVITY_TIMEOUT);
  };

  // Set up activity listeners and periodic status updates
  useEffect(() => {
    if (isAuthenticated) {
      // Initial status update
      updateOnlineStatus(true);

      // Set up event listeners for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity, true);
      });

      // Set initial inactivity timer
      activityTimerRef.current = setTimeout(() => {
        setIsOnline(false);
        updateOnlineStatus(false);
      }, INACTIVITY_TIMEOUT);

      // Set up periodic status updates (heartbeat) while online
      statusUpdateTimerRef.current = setInterval(() => {
        if (isOnline) {
          updateOnlineStatus(true);
        }
      }, STATUS_UPDATE_INTERVAL);

      // Update status to offline when window is closing or user navigates away
      const handleUnload = () => {
        // Use synchronous approach for beforeunload
        const token = Cookies.get('token'); // Get directly from cookie
        
        if (token) {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          navigator.sendBeacon(
            `${apiUrl}/user/update_status`,
            JSON.stringify({ 
              is_online: false,
              token: token 
            })
          );
        }
      };
      window.addEventListener('beforeunload', handleUnload);

      // Clean up
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity, true);
        });
        if (activityTimerRef.current) {
          clearTimeout(activityTimerRef.current);
        }
        if (statusUpdateTimerRef.current) {
          clearInterval(statusUpdateTimerRef.current);
        }
        window.removeEventListener('beforeunload', handleUnload);

        // Mark as offline when unmounting if we're still online
        if (isOnline) {
          updateOnlineStatus(false);
        }
      };
    }
  }, [isAuthenticated]);

  // No UI rendering for this component
  return null;
};

const MainLayout = () => {
  const { isChatOpen, isChatLocked } = useChat();
  const { autoHideSidebar, isSidebarOpen, setIsSidebarOpen } = useContext(SettingsContext);
  const location = useLocation();
  const isEsdCheckPage = location.pathname === "/esd-check";
  const showChatPanel = location.pathname !== "/login" && location.pathname !== "/social";

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Adjust margin when chat is open OR locked
  const marginRight = location.pathname === "/login" ? "0" : (isChatLocked ? "400px" : "0");
  const isDashboard = location.pathname === "/dashboard";

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: "100vh", 
      position: "relative", 
      overflowX: "hidden",
      overflow: isDashboard ? 'hidden !important' : 'auto'
    }}>
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {!isEsdCheckPage && (autoHideSidebar ? (isSidebarOpen && <Sidebar />) : <Sidebar />)}
        {!isEsdCheckPage && autoHideSidebar && (
          <SidebarToggleButton 
            isOpen={isSidebarOpen} 
            onToggle={toggleSidebar} 
          />
        )}
        {!isEsdCheckPage && <FloatingSettingsPanel />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "background.default",
            color: "text.primary",
            p: isEsdCheckPage ? 0 : 3,
            marginRight: isEsdCheckPage ? 0 : marginRight,
            transition: theme => theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            width: isEsdCheckPage ? '100%' : `calc(100% - ${marginRight})`,
            overflow: isDashboard ? 'hidden !important' : 'auto',
            position: 'relative',
          }}
        >
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Activities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Stock />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipping"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Shipping />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workers"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin', 'workers']}>
                  <WorkerManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin"
              element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <SuperAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repair-cost"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin', 'repair-cost', 'price-admin']}>
                  <RepairCostChecker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/price-admin"
              element={
                <ProtectedRoute allowedRoles={['price-admin', 'superadmin']}>
                  <PriceAdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/social"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                  <Social />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route
              path="/esd-overview"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']} requiredFunction="esdoverview">
                  <EsdOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/esd-check"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superadmin']} requiredFunction="esdregister">
                  <EsdCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/front"
              element={
                <ProtectedRoute allowedRoles={['developer']}>
                  <Front />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/back"
              element={
                <ProtectedRoute allowedRoles={['developer', 'superadmin']} requiredFunction="systemdocumentation">
                  <Back />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/feedback"
              element={
                <ProtectedRoute allowedRoles={['developer', 'superadmin']} allowPublic>
                  <Feedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/feedbackreader"
              element={
                <ProtectedRoute allowedRoles={['developer', 'superadmin']} requiredFunction="systemdocumentation">
                  <FeedbackReader />
                </ProtectedRoute>
              }
            />
            <Route
              path="/webork-server"
              element={
                <ProtectedRoute allowedRoles={['superadmin', 'developer', 'admin']}>
                  <WebOrkDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to={isEsdCheckPage ? "/esd-check" : "/login"} />} />
          </Routes>
        </Box>
      </Box>
      {!isEsdCheckPage && <StatusBar />}
      {showChatPanel && !isEsdCheckPage && <ChatPanel />}
    </Box>
  );
};

export default App;
